import * as THREE from "three";
import React, {
    useRef,
    useMemo,
    useState,
    useCallback,
    useEffect,
} from "react";
import {useGLTF, useTexture, Html} from "@react-three/drei";
import {animated as a, useSpring, config} from "@react-spring/three";
import {useFrame} from "@react-three/fiber";
import {GLTF} from "three/examples/jsm/loaders/GLTFLoader";
import {Button} from "@chakra-ui/react";
import {Persona, Presentation} from "../data/types";
import {useStore} from "../store";

import {ReactComponent as CloseSVG} from "../assets/images/close_heavy.svg";

import {RedwoodPalette as colors} from "../data/constants";
import { PresentationContext }  from "./Board"


const frameEmissionColor = new THREE.Color(colors.brandNeutral10)
const avatarPath = "/models/avatar/avatar.gltf"

type GLTFResult = GLTF & {
    nodes: {
        frame: THREE.Mesh;
        canvas: THREE.Mesh;
    };
};

// I already regret not leaving comments, sorry future Art
// it's okay, I still love you.

export default function Avatar(
    props: JSX.IntrinsicElements["mesh"] & {
        index: number;
        persona: Persona;
        count: number;
        takenPoints: any
    }
) {
    const context = React.useContext( PresentationContext) as unknown
    const presentation = context as Presentation
    const rotationSpeedModifier = presentation.animationSpeed ? (presentation.animationSpeed / 2 ) * 0.01 : 0.05

    const [hovered, setHovered] = useState(false);
    const group = useRef<THREE.Group>();
    const lastClockPosition = useRef<number>(0);
    const selectedPersona = useStore((state) => state["selectedPersona"]);
    const disabledAnchorPositions = useStore((state) => state["disabledAnchorPositions"]);
    const isLandingBlurred = useStore((state) => state["isLandingBlurred"]);
    const setSelectedPersona = useStore((state) => state["setSelectedPersona"]);
    const isGalleryMode = useStore((state) => state["isGalleryMode"]);
    const selectedAnchor = useStore((state) => state["selectedAnchorPosition"]);
    const animatedGroup = useRef<any>();
    const buttonRef = useRef<HTMLButtonElement>(null)
    const {nodes} = useGLTF(avatarPath) as unknown as GLTFResult;
    const position = getRadialPosition(props.index, props.count);
    const active = selectedPersona === props.persona.key;
    const inactive = selectedPersona === null;
    const [animated, set] = useSpring<{
        position: [number, number, number];
        rotationX: number;
        rotationY: number;
        rotationZ: number;
        scale: number;
        opacity: number;
    }>(() => ({
        position: position.toArray(),
        scale: 0.25,
        rotationX: 0,
        rotationY: 0,
        rotationZ: 0,
        opacity: 1,
        config: config.default,
    }));
    const avatarTexture = useTexture( props.persona.avatarFile);
    avatarTexture.flipY = false;
    const handleClick = useCallback(
        (e) => {
            e.stopPropagation();
            setSelectedPersona(props.persona.key);
            setHovered(false);
        },
        [props.persona.key, setSelectedPersona]
    );

    const handleToggle = useCallback(
        (e) => {
            e.stopPropagation();
            setSelectedPersona(null);
            setHovered(false);
        },
        [setSelectedPersona]
    );

    useEffect(() => {
        document.body.style.cursor = hovered ? "pointer" : "auto";
        if (buttonRef.current) {
            buttonRef.current.style.cursor = hovered ? "pointer" : "auto";
        }
    }, [hovered]);


    const random = useMemo(() => !isGalleryMode ? rand(-2, 2) : Number.EPSILON, [isGalleryMode]);

    // Handles scaling on deactivate
    const randScale = rand(0.12, 0.18);
    const ROTATION_SPEED = !isGalleryMode ? rotationSpeedModifier : Number.EPSILON;

    // Finds the closest anchor point and updates the current taken anchor point
    const closestAnchor = useCallback(() => {
        const closestAnchorPosition = getNearestDisabledAnchor(
            disabledAnchorPositions,
            getRadialPosition(
                props.index,
                props.count,
                lastClockPosition.current * ROTATION_SPEED
            ),
            props.takenPoints,
        )

        props.takenPoints.current = [...props.takenPoints.current, closestAnchorPosition]

        return closestAnchorPosition
        // eslint-disable-next-line
    }, [disabledAnchorPositions])

    useEffect(() => {
        // resets available anchor points
        if (active) {
            props.takenPoints.current = []
        }
        // eslint-disable-next-line
    }, [active, disabledAnchorPositions])

    useFrame(({clock}) => {
        let a = !isGalleryMode ? clock.getElapsedTime() : 0.0001;
        const position = getRadialPosition(
            props.index,
            props.count,
            a * ROTATION_SPEED
        );

        if (inactive && group.current) {
            set({
                position: position.toArray(),
                rotationX: Number.EPSILON,
                rotationY: Number.EPSILON,
                rotationZ: Number.EPSILON,
                opacity: 1,
                scale: 0.25,
            });

            const scale = Math.abs(Math.sin(a) * random * 0.05 + .75);
            group.current.scale.set(scale, scale, scale);
            group.current.rotation.z += Math.sin(a + 0.5) * 0.001 * random;
            group.current.position.y += Math.sin(a) * 0.0005 * random;
            lastClockPosition.current = a;
        } else if (group.current && props.takenPoints.current.length < disabledAnchorPositions.length) {
            // to optimize performance, only set when disabled
            set({
                position: closestAnchor().toArray(),
                opacity: 0.5,
                scale: randScale,
            });
        }
    })

    useFrame(() => {
        if (active && group.current) {
            set({
                position: [selectedAnchor.x,selectedAnchor.y,selectedAnchor.z],
                rotationX: 0,
                rotationY: 0,
                rotationZ: 0,
                opacity: 1,
                scale: 0.25,
            });

            group.current.scale.set(1, 1, 1);
            group.current.rotation.z = 0;
            group.current.rotation.y = 0;
        }
    })

    // Hover effect
    useFrame(({clock}) => {
        let a = !isGalleryMode ? clock.getElapsedTime() : Number.EPSILON;
        const position = getRadialPosition(
            props.index,
            props.count,
            a * ROTATION_SPEED
        );

        if (hovered && inactive && group.current) {
            set({
                position: position.toArray(),
                rotationX: Number.EPSILON,
                rotationY: Number.EPSILON,
                rotationZ: Number.EPSILON,
                opacity: 1,
                scale: 0.28,
            });

            const scale = Math.abs(Math.sin(a) * random * 0.05 + .75);
            group.current.scale.set(scale, scale, scale);
            group.current.rotation.z += Math.sin(a + 0.5) * 0.001 * random;
            group.current.position.y += Math.sin(a) * 0.0005 * random;

            lastClockPosition.current = a;
        }
    })

    return (
        <a.group
            ref={animatedGroup}
            position={animated.position}
            rotation-x={animated.rotationX}
            rotation-y={animated.rotationY}
            rotation-z={animated.rotationZ}
            scale={animated.scale}
            onClick={handleClick}
        >
            <group
                ref={group}
                dispose={null}
                rotation={[Math.PI / 2, 0, 0]}
                position-z={-0.001}
                onPointerOver={() => setHovered(true)}
                onPointerOut={() => setHovered(false)}
            >
                <mesh geometry={nodes.frame.geometry}>
                    {/* @ts-ignore */}
                    <a.meshStandardMaterial
                        color={colors.brandNeutral10}
                        emissive={frameEmissionColor}
                        emissiveIntensity={0.8}
                        transparent
                        side={THREE.DoubleSide}
                        opacity={animated.opacity}
                    />
                </mesh>
                <mesh geometry={nodes.canvas.geometry}>
                    <a.meshToonMaterial
                        map={avatarTexture}
                        transparent
                        side={THREE.DoubleSide}
                        opacity={animated.opacity}
                    />
                    <Html
                        style={{
                            pointerEvents: "none",
                            userSelect: "none",
                            opacity: inactive ? 1 : 0,
                            transition: "all 0.2s ease",
                            transform: "translateX(-50%)",
                        }}
                        distanceFactor={2}
                        position-z={1.1}
                        zIndexRange={[0, 100]}
                    >

                        <p
                            style={{
                                color: colors.brandNeutral10,
                                margin: 0,
                                whiteSpace: "nowrap",
                                textAlign: 'center',
                                filter: isGalleryMode || isLandingBlurred ? "blur(25px)" : "",
                            }}
                        >
                            {props.persona.name.split(' ')[0]}
                        </p>
                        <p
                            style={{
                                color: colors.brandNeutral10,
                                margin: 0,
                                fontSize: 18,
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                                textAlign: 'center',
                                filter: isGalleryMode || isLandingBlurred ? "blur(25px)" : "",
                            }}
                        >
                            {props.persona.title}
                        </p>
                    </Html>
                    <Html
                        style={{
                            pointerEvents: "none",
                            userSelect: "none",
                            opacity: active ? 1 : 0,
                            transition: active ? "all 0.5s ease 0.3s" : "all 0.2s ease 0s",
                            transform: "translateY(-50%)",
                        }}
                        distanceFactor={2}
                        position-z={0}
                        position-x={1.1}
                        zIndexRange={[0, 100]}
                    >
                        <p
                            style={{
                                color: colors.brandNeutral10,
                                marginLeft: 10,
                                marginTop: 0,
                                marginBottom: 0,
                                fontSize: 18,
                                whiteSpace: "nowrap",
                                userSelect: "none",
                                filter: isGalleryMode || isLandingBlurred ? "blur(25px)" : "",
                            }}
                        >
                            {props.persona.name}
                        </p>
                        <p
                            style={{
                                color: colors.brandNeutral10,
                                marginLeft: 10,
                                marginTop: 0,
                                marginBottom: 12,
                                fontSize: 25,
                                whiteSpace: "nowrap",
                                filter: isGalleryMode || isLandingBlurred ? "blur(25px)" : "",
                            }}
                        >
                            {props.persona.title}
                        </p>
                        <p
                            style={{
                                color: colors.brandNeutral10,
                                marginLeft: 10,
                                marginTop: 0,
                                marginBottom: 0,
                                fontSize: 18,
                                lineHeight: 1.6,
                                width: 380,
                                userSelect: "none",
                                filter: isGalleryMode || isLandingBlurred ? "blur(25px)" : "",
                            }}
                        >
                            {props.persona.description}
                        </p>
                        <Button
                            variant="unstyled"
                            position="absolute"
                            ref={buttonRef}
                            top={0}
                            right={-60}
                            pointerEvents="all"
                            onClick={handleToggle}
                            filter={isGalleryMode || isLandingBlurred ? "blur(2px)" : undefined}
                            onPointerOver={() => setHovered(true)}
                            onPointerOut={() => setHovered(false)}
                            style={{
                                cursor: 'pointer'
                            }}
                        >
                            <CloseSVG width="50px" height="50px" />
                        </Button>
                    </Html>
                </mesh>
            </group>
        </a.group>
    );
}

const getRadiusOffset = (count: number): number => {
    switch (count) {
        case 1:
            return Math.PI * 1.5;
        case 2:
            return Math.PI * 2;
        case 3:
            return Math.PI / 6;
        case 4:
            return Math.PI / 4;
        case 5:
            return Math.PI / 3.33;
        case 6:
            return Math.PI / 3;
        case 7:
            return Math.PI / 2.8;
        case 8:
            return Math.PI / 2.66;
        case 9:
            return Math.PI / 2.6;
        case 10:
            return Math.PI / 2.5;
        default:
            return 0;
    }
};

const getRadialPosition = (
    index: number,
    count: number,
    startAngle = 0,
    radius = 0.7,
    offset = {x: 0, y: 0, z: 0.2}
) => {
    const i = index;
    const radiusOffset = getRadiusOffset(count) + startAngle;
    const angle =
        ((i % count) / count) * Math.PI * 2 + Math.PI / count + radiusOffset;
    return new THREE.Vector3(
        Math.cos(angle) * radius + offset.x,
        Math.sin(angle) * radius + offset.y,
        offset.z
    );
};

function rand(min: number, max: number) {
    return Math.random() * (max - min) + min;
}

// ensures vectors don't overlap
const getVectorCalculation = (sortedVectors: THREE.Vector3[], takenPoints: { current: THREE.Vector3[] }, incrementor: number): THREE.Vector3 => {
    const sortedVectorsArr = sortedVectors[incrementor].toArray()
    const newVector = new THREE.Vector3(...sortedVectorsArr)

    // check if our new vector exists in the current taken anchor points
    if (takenPoints.current.findIndex((currVector) => currVector.equals(newVector)) === -1) {
        return newVector
    } else {
        // if it does exist, then calculate new vector point
        if (incrementor < sortedVectors.length - 1) {
            return getVectorCalculation(sortedVectors, takenPoints, incrementor += 1)
        } else {
            return new THREE.Vector3()
        }
    }
}

const getNearestDisabledAnchor = (
    disabledAnchorPositions: THREE.Vector3[],
    position: THREE.Vector3,
    takenPoints: { current: THREE.Vector3[] },
) => {
    // sorts vectors from closest to farthest disabled anchor point and determines where to send disabled avatar
    const sortedVectors = disabledAnchorPositions.sort((a, b) =>
        position.distanceToSquared(a) > position.distanceToSquared(b) ? 1 : -1
    )
    const vector = getVectorCalculation(sortedVectors, takenPoints, 0)
    return vector
};

useGLTF.preload(avatarPath);
